import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import dynamic from 'next/dynamic'

import styles from './style.module.scss'
import { tableLoader } from '@shared/libs/allLoader'
import useTranslation from 'next-translate/useTranslation'
import BatterDataRow from './BatterDataRow'
import { getMatchPlayers } from '@shared/libs/match-detail'

const ThemeTable = dynamic(() => import('@shared-components/themeTable'), { loading: () => tableLoader() })
const CustomLink = dynamic(() => import('@shared/components/customLink'))

const ScorecardTable = ({ data, fullSquad, matchDetail }) => {
  const allPlayers = getMatchPlayers()
  const { t } = useTranslation()
  const labels = [`${t('common:Batter')}`, t('common:R'), t('common:B'), `${t('common:4s')}`, `${t('common:6s')}`, `${t('common:SR')}`]
  const table = useRef({ nRuns: 'Runs', nBallFaced: 'Balls', nFours: '4s', nSixes: '6s', sStrikeRate: 'SR' })

  return (
    <div className={`${styles.scorecardTable}`}>
      <ThemeTable labels={labels} isBordered={true}>
        {data?.aBatters?.map((batter, index) => {
          return (
            <BatterDataRow key={index} batter={batter} table={table} />
          )
        })}
        <tr>
          {data?.aYetToBat?.length > 0 && (
            <td colSpan="6" className={`${styles.YetToBat}`}>
              {matchDetail?.sStatusStr === 'completed' ? (
                <span className="text-uppercase fw-bold">{t('common:DidNotBat')}: </span>
              ) : (
                <span className="text-uppercase fw-bold">{t('common:YetToBat')}: </span>
              )}
              <span className={`${styles.subText} font-medium text-wrap ms-1`}>
                {data?.aYetToBat?.map((p, i) => {
                  const player = allPlayers[p?._id]
                  return (
                    <span key={player?._id}>
                      {i ? ', ' : ''}
                      {player?.eTagStatus === 'a' && player?.oSeo?.sSlug ? (
                        <CustomLink href={`/${player?.oSeo?.sSlug}`}>
                          <>
                            {player?.sFullName || player?.sFirstName}
                            {player?.sRoleStr === 'wk' && ` (${t('common:Wk')})`}
                            {player?.sSplRoleStr === 'cap' && ` (${t('common:c')})`}
                            {(player?.sRoleStr === 'wk' && player?.sSplRoleStr === 'cap') && ` (${t('common:WkCap')})`}
                          </>
                        </CustomLink>
                      ) : (
                        <>
                          {player?.sFullName || player?.sFirstName}
                          {player?.sRoleStr === 'wk' && ` (${t('common:Wk')})`}
                          {player?.sSplRoleStr === 'cap' && ` (${t('common:c')})`}
                          {(player?.sRoleStr === 'wk' && player?.sSplRoleStr === 'cap') && ` (${t('common:WkCap')})`}
                        </>
                      )}
                    </span>
                  )
                })}
              </span>
            </td>
          )}
        </tr>
        <tr>
          <td className="text-uppercase fw-bold">
            <span className="d-flex flex-column flex-md-row">
              <span>{t('common:Extra')}</span>
              <span className={`${styles.subText} font-medium`}>
                ( {t('common:b')} - {data?.oExtraRuns?.nByes}, {t('common:w')} - {data?.oExtraRuns?.nWides}, {t('common:no')} -{' '}
                {data?.oExtraRuns?.nNoBalls}, {t('common:lb')} - {data?.oExtraRuns?.nLegByes}, {t('common:p')} -{' '}
                {data?.oExtraRuns?.nPenalty === null ? '0' : data?.oExtraRuns?.nPenalty} )
              </span>
            </span>
          </td>
          <td>{data?.oExtraRuns?.nTotal}</td>
          <td colSpan="4"> </td>
        </tr>
        <tr className="highlight">
          <td className="text-uppercase fw-bold">
            <span className="d-flex flex-column flex-md-row">
              <span>{t('common:Total')}</span>
              <span className={`${styles.subText} font-medium`}>
                ( {data?.oEquations?.sRunRate} {t('common:RunsPerOver')} )
              </span>
            </span>
          </td>
          <td>
            {data?.oEquations?.nRuns}
            {(data?.oEquations?.sOvers && data?.oEquations?.nWickets >= 0) && ` (${data?.oEquations?.nWickets} ${t('common:Wkts')}, ${data?.oEquations?.sOvers} ${t('common:Ov')})`}
          </td>
          <td colSpan="4"> </td>
        </tr>
      </ThemeTable>
    </div>
  )
}

ScorecardTable.propTypes = {
  data: PropTypes.object,
  fullSquad: PropTypes.array,
  matchDetail: PropTypes.object
}

export default ScorecardTable
